'use client';
import React, { useState, useEffect } from 'react';
import { X } from 'lucide-react';
import AdComponent from '../AdComponent';

const AdModal: React.FC = () => {
  const [showModal, setShowModal] = useState(false);
  const [adAvailable, setAdAvailable] = useState(false);
  const [modalAdLoaded, setModalAdLoaded] = useState<boolean | null>(null); // null = loading, true = loaded, false = failed
  const isDevelopment = process.env.NODE_ENV === 'development' || 
                       (typeof window !== 'undefined' && window.location.hostname === 'localhost');
  
  const MODAL_SHOWN_KEY = 'adModal_shown_this_session';

  const handleAdLoaded = (loaded: boolean) => {
    if (loaded) {
      setAdAvailable(true);
      const hasShownModal = sessionStorage.getItem(MODAL_SHOWN_KEY);
      if (!hasShownModal) {
        setShowModal(true);
        sessionStorage.setItem(MODAL_SHOWN_KEY, 'true');
        setModalAdLoaded(null); // Reset when modal opens
      }
    }
  };

  const handleModalAdLoaded = (loaded: boolean) => {
    setModalAdLoaded(loaded);
  };

  const handleCloseModal = () => {
    setShowModal(false);
  };

  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && showModal) {
        handleCloseModal();
      }
    };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [showModal]);
//Scroll disable for background interactions when modal is open
  useEffect(() => {
    if (showModal) {
      const scrollY = window.scrollY;
      document.body.style.position = 'fixed';
      document.body.style.top = `-${scrollY}px`;
      document.body.style.width = '100%';
      document.body.style.overflow = 'hidden';

      return () => {
        document.body.style.position = '';
        document.body.style.top = '';
        document.body.style.width = '';
        document.body.style.overflow = '';
        window.scrollTo(0, scrollY);
      };
    }
  }, [showModal]);

  return (
    <>
      <div style={{ 
        position: 'absolute', 
        left: '-9999px', 
        top: '-9999px', 
        width: '300px', 
        height: '250px',
        overflow: 'hidden'
      }}>
        <AdComponent
          index={2}
          identifier={'website_openapp_top'}
          onAdLoaded={handleAdLoaded}
        />
      </div>

      {showModal && (
        <div
          className='fixed inset-0 bg-black/50 z-[9999] flex items-center justify-center p-4 overflow-hidden'
          onClick={handleCloseModal}
          style={{ touchAction: 'none' }}
        >
          <div
            className='bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] z-[10000] relative flex flex-col'
            onClick={(e) => e.stopPropagation()}
          >
            {/* Fixed Close Button */}
            <button
              onClick={handleCloseModal}
              className='absolute top-4 right-4 z-20 bg-gray-200 hover:bg-gray-300 rounded-full p-2 transition-colors'
              aria-label='Close modal'
            >
              <X className='h-6 w-6 text-gray-700' />
            </button>

            {/* Scrollable Content */}
            <div className='overflow-y-auto flex-1 p-8'>
              {adAvailable ? (
                <div style={{ minHeight: '250px', position: 'relative' }}>
                  <AdComponent
                    index={3}
                    identifier={'website_openapp_top'}
                    onAdLoaded={handleModalAdLoaded}
                  />
                  {modalAdLoaded === null && (
                    <div className='absolute inset-0 flex items-center justify-center bg-white/80 rounded-lg'>
                      <p className='text-gray-500 text-sm'>Loading ad...</p>
                    </div>
                  )}
                  {modalAdLoaded === false && (
                    <div className='absolute inset-0 flex items-center justify-center bg-white/80 rounded-lg'>
                      <p className='text-gray-500 text-sm'>Ad failed to load</p>
                    </div>
                  )}
                </div>
              ) : isDevelopment ? (
                <div className='border-2 border-dashed border-gray-300 rounded-lg p-8 text-center bg-gray-50'>
                  <p className='text-gray-600 mb-2'>Ad Placeholder (Test Mode)</p>
                  <p className='text-sm text-gray-500'>Ad would appear here in production</p>
                </div>
              ) : null}
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default AdModal;

